import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, ChevronLeft, ChevronRight, ShoppingBasket } from 'lucide-react';
import NavBar from '../components/nav';
import { Footer } from '../components/footer';
import { useCart } from '../contexts/CartContext';
import useGroceryFeed from '../hooks/useGroceryFeed';
import GroceryShopSection from '../components/home/sections/GroceryShopSection';
import GroceryShopsCarousel from '../components/home/sections/GroceryShopsCarousel';
import { getGroceryCategoryIcon } from '../utils/groceryCategoryIcon';
import namkeFallback from '../assets/namke-fallback.png';

const ITEMS_PER_PAGE = 12;

const formatPrice = (p) => {
  const n = typeof p === 'string' ? parseFloat(p) : Number(p);
  if (Number.isNaN(n)) return '—';
  return `${n.toFixed(2).replace('.', ',')} €`;
};

const shopInitials = (name) =>
  (name || '?').split(' ').slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('');

const BestSellerCard = ({ product, onQuickAdd }) => {
  const { t } = useTranslation();
  const href = product.source === 'dish' ? `/product/${product.id}` : `/grocery-shop-product/${product.id}`;
  return (
    <Link to={href} className="group shrink-0 snap-start w-40 sm:w-48">
      <div className="relative w-40 h-40 sm:w-48 sm:h-48 rounded-xl overflow-hidden bg-namke-cream">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => { e.target.src = namkeFallback; }}
          />
        ) : (
          <img src={namkeFallback} alt="" className="w-full h-full object-cover" />
        )}
        <button
          type="button"
          onClick={(e) => onQuickAdd(e, product)}
          aria-label={t('store.addToCart')}
          className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-white shadow-md text-namke-black flex items-center justify-center hover:bg-namke-cream transition-colors"
        >
          <Plus size={18} />
        </button>
      </div>
      <p className="mt-2 text-base font-bold text-namke-black">{formatPrice(product.price)}</p>
      <p className="text-sm text-namke-black/55 line-clamp-2">{product.name}</p>
    </Link>
  );
};

const CategoryChip = ({ label, icon, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex-shrink-0 flex flex-col items-center gap-2 px-1 py-1 rounded-[22px] text-center w-[76px]"
  >
    <span
      className={`w-14 h-14 rounded-full flex items-center justify-center overflow-hidden ring-2 transition-colors ${
        active ? 'ring-namke-terracotta' : 'ring-transparent'
      }`}
      style={{ backgroundColor: active ? 'rgba(239,76,35,0.12)' : 'rgb(251,249,217)' }}
    >
      <img src={icon} alt="" className="w-full h-full object-cover" />
    </span>
    <span className={`text-xs leading-tight ${active ? 'font-semibold text-namke-black' : 'text-namke-black/75'}`}>
      {label}
    </span>
  </button>
);

const Store = () => {
  const { t } = useTranslation();
  const { addToCart } = useCart();
  const {
    groceryShops,
    selectedGroceryShopId,
    setSelectedGroceryShopId,
    selectedGroceryCategory,
    groceryShopLoading,
    groceryCategories,
    filteredGroceryProducts,
    handleGroceryCategoryClick,
  } = useGroceryFeed();

  const selectedShop = groceryShops.find((s) => s.id === selectedGroceryShopId) || null;
  const bestSellers = (filteredGroceryProducts || []).slice(0, 10);
  const scrollerRef = React.useRef(null);

  // Bornes de prix dérivées du catalogue (filtré magasin + catégorie), pour le curseur "Prix maximum".
  const priceBounds = React.useMemo(() => {
    const prices = (filteredGroceryProducts || [])
      .map((p) => (typeof p.price === 'string' ? parseFloat(p.price) : Number(p.price)))
      .filter((n) => !Number.isNaN(n));
    if (prices.length === 0) return { min: 0, max: 30 };
    return { min: 0, max: Math.max(1, Math.ceil(Math.max(...prices))) };
  }, [filteredGroceryProducts]);

  const [maxPrice, setMaxPrice] = React.useState(priceBounds.max);
  const [africanOnly, setAfricanOnly] = React.useState(false);
  const [inStockOnly, setInStockOnly] = React.useState(false);
  const [sortBy, setSortBy] = React.useState('default');
  const [page, setPage] = React.useState(1);
  const priceTouchedRef = React.useRef(false);

  // Tant que l'utilisateur n'a pas touché au curseur, il suit les nouvelles bornes (catalogue
  // complet visible par défaut) ; une fois touché, on ne réaligne que s'il dépasse le nouveau max.
  React.useEffect(() => {
    setMaxPrice((prev) =>
      !priceTouchedRef.current || prev === undefined || prev > priceBounds.max ? priceBounds.max : prev,
    );
  }, [priceBounds.max]);

  const clearFilters = () => {
    priceTouchedRef.current = false;
    setMaxPrice(priceBounds.max);
    setAfricanOnly(false);
    setInStockOnly(false);
  };

  const filtersActive = africanOnly || inStockOnly || maxPrice < priceBounds.max;

  const visibleProducts = React.useMemo(() => {
    let list = (filteredGroceryProducts || []).filter((p) => {
      const price = typeof p.price === 'string' ? parseFloat(p.price) : Number(p.price);
      if (!Number.isNaN(price) && price > maxPrice) return false;
      if (africanOnly && !p.is_african) return false;
      if (inStockOnly && p.stock_quantity != null && p.stock_quantity <= 0) return false;
      return true;
    });
    if (sortBy === 'price_asc') {
      list = [...list].sort((a, b) => Number(a.price) - Number(b.price));
    } else if (sortBy === 'price_desc') {
      list = [...list].sort((a, b) => Number(b.price) - Number(a.price));
    }
    return list;
  }, [filteredGroceryProducts, maxPrice, africanOnly, inStockOnly, sortBy]);

  const totalPages = Math.max(1, Math.ceil(visibleProducts.length / ITEMS_PER_PAGE));
  const pageSafe = Math.min(page, totalPages);
  const pagedProducts = visibleProducts.slice((pageSafe - 1) * ITEMS_PER_PAGE, pageSafe * ITEMS_PER_PAGE);

  React.useEffect(() => {
    setPage(1);
  }, [maxPrice, africanOnly, inStockOnly, sortBy, selectedGroceryCategory, selectedGroceryShopId]);

  const handlePageChange = (p) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollByPage = (direction) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.9, behavior: 'smooth' });
  };

  const handleQuickAdd = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image_url,
      restaurantName: product.restaurant_name,
      quantity: 1,
    });
  };

  return (
    <>
      <NavBar />
      <div className="min-h-screen bg-namke-white">
        {/* Bandeau héro */}
        <div className="pt-20 relative overflow-hidden" style={{ backgroundColor: 'rgb(251,249,217)' }}>
          <div
            className="absolute rounded-full opacity-10 pointer-events-none"
            style={{ right: -110, bottom: -150, width: 340, height: 340, backgroundColor: 'rgb(239,76,35)' }}
          />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
            {selectedShop ? (
              <div className="flex items-center gap-4">
                {selectedShop.image_url ? (
                  <img
                    src={selectedShop.image_url}
                    alt=""
                    className="w-16 h-16 rounded-full object-cover border-2 border-namke-white bg-namke-white shrink-0"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-namke-darkGreen/10 flex items-center justify-center text-xl font-bold text-namke-darkGreen shrink-0">
                    {shopInitials(selectedShop.name)}
                  </div>
                )}
                <div className="min-w-0">
                  <h1 className="font-display text-2xl sm:text-3xl font-black text-namke-black truncate tracking-tight">
                    {selectedShop.name}
                  </h1>
                  <p className="text-namke-black/76 text-sm mt-1 max-w-xl line-clamp-2">
                    {selectedShop.description || t('store.defaultDescription')}
                  </p>
                  <p className="text-namke-black/62 text-xs mt-1">
                    {t('store.productCount', { count: filteredGroceryProducts.length })}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-4 max-w-2xl">
                <span className="font-sub text-[13px] font-semibold uppercase tracking-[0.1em] text-namke-terracotta flex items-center gap-3">
                  <ShoppingBasket size={16} />
                  {t('store.eyebrow')}
                </span>
                <h1 className="font-display font-black text-[clamp(28px,4.5vw,48px)] leading-[0.98] tracking-tight text-namke-black">
                  {t('store.heroTitle')}
                </h1>
                <p className="text-namke-black/76 text-base sm:text-lg leading-relaxed">
                  {t('store.heroSubtitle')}
                </p>
                <div className="flex flex-wrap gap-2.5 mt-1.5">
                  <span className="font-sub text-[13px] bg-namke-white px-4 py-2 rounded-full">{t('store.pillBundled')}</span>
                  <span className="font-sub text-[13px] bg-namke-white px-4 py-2 rounded-full">{t('store.pillFreeShipping')}</span>
                  <span className="font-sub text-[13px] bg-namke-white px-4 py-2 rounded-full">{t('store.pillDeposit')}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Bande de catégories */}
          {groceryCategories.length > 0 && (
            <div className="mb-6">
              <div
                className="flex gap-3 overflow-x-auto snap-x snap-proximity scroll-smooth pb-2 [&::-webkit-scrollbar]:hidden"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                <CategoryChip
                  label={t('store.all')}
                  icon={getGroceryCategoryIcon('Tous')}
                  active={!selectedGroceryCategory}
                  onClick={() => handleGroceryCategoryClick('Tous')}
                />
                {groceryCategories.map((category) => (
                  <CategoryChip
                    key={category}
                    label={category}
                    icon={getGroceryCategoryIcon(category)}
                    active={selectedGroceryCategory === category}
                    onClick={() => handleGroceryCategoryClick(category)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Sélecteur de magasins */}
          <GroceryShopsCarousel
            shops={groceryShops}
            selectedShopId={selectedGroceryShopId}
            onSelectShop={setSelectedGroceryShopId}
          />

          {/* Meilleures ventes */}
          {!groceryShopLoading && bestSellers.length > 0 && (
            <div className="mt-2 mb-10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-xl sm:text-2xl font-black text-namke-black tracking-tight">{t('store.bestSellers')}</h2>
                <div className="hidden sm:flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => scrollByPage(-1)}
                    aria-label={t('store.previous')}
                    className="w-9 h-9 rounded-full bg-namke-cream hover:bg-namke-cream text-namke-black/75 flex items-center justify-center transition-colors"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => scrollByPage(1)}
                    aria-label={t('store.next')}
                    className="w-9 h-9 rounded-full bg-namke-cream hover:bg-namke-cream text-namke-black/75 flex items-center justify-center transition-colors"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
              <div
                ref={scrollerRef}
                className="flex gap-4 overflow-x-auto pb-2 snap-x snap-proximity scroll-smooth [&::-webkit-scrollbar]:hidden"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {bestSellers.map((product) => (
                  <BestSellerCard
                    key={`${product.source || 'catalog'}-${product.id}`}
                    product={product}
                    onQuickAdd={handleQuickAdd}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Filtres + catalogue */}
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 lg:gap-9 items-start">
            <aside className="bg-namke-cream rounded-[32px] p-6 flex flex-col gap-5 lg:sticky lg:top-24">
              <div className="flex items-center gap-2.5">
                <strong className="font-display font-semibold text-[19px] text-namke-black">{t('store.filtersTitle')}</strong>
                {filtersActive && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="ml-auto font-sub text-[13px] font-semibold text-namke-terracotta underline"
                  >
                    {t('store.clearFilters')}
                  </button>
                )}
              </div>

              <div className="flex flex-col gap-2.5">
                <span className="font-sub text-[12.5px] font-semibold uppercase tracking-[0.09em] text-namke-black/60">
                  {t('store.maxPrice')}
                </span>
                <input
                  type="range"
                  min={priceBounds.min}
                  max={priceBounds.max}
                  step="1"
                  value={maxPrice}
                  onChange={(e) => {
                    priceTouchedRef.current = true;
                    setMaxPrice(Number(e.target.value));
                  }}
                  aria-label={t('store.maxPrice')}
                  className="w-full accent-namke-terracotta"
                />
                <span className="text-[13.5px] text-namke-black/70">{t('store.upTo', { amount: formatPrice(maxPrice) })}</span>
              </div>

              <label className="flex items-center gap-2.5 text-[14.5px] text-namke-black">
                <input
                  type="checkbox"
                  checked={africanOnly}
                  onChange={(e) => setAfricanOnly(e.target.checked)}
                  className="w-[17px] h-[17px] accent-namke-darkGreen"
                />
                {t('store.africanOnly')}
              </label>

              <div className="flex flex-col gap-2.5">
                <span className="font-sub text-[12.5px] font-semibold uppercase tracking-[0.09em] text-namke-black/60">
                  {t('store.availability')}
                </span>
                <label className="flex items-center gap-2.5 text-[14.5px]">
                  <input
                    type="radio"
                    name="ne-dispo"
                    checked={!inStockOnly}
                    onChange={() => setInStockOnly(false)}
                    className="w-[17px] h-[17px] accent-namke-terracotta"
                  />
                  {t('store.allAvailability')}
                </label>
                <label className="flex items-center gap-2.5 text-[14.5px]">
                  <input
                    type="radio"
                    name="ne-dispo"
                    checked={inStockOnly}
                    onChange={() => setInStockOnly(true)}
                    className="w-[17px] h-[17px] accent-namke-terracotta"
                  />
                  {t('store.inStockOnly')}
                </label>
              </div>
            </aside>

            <div className="flex flex-col gap-5 min-w-0">
              <div className="flex items-baseline gap-3.5 flex-wrap">
                <h2 className="font-display font-black text-[clamp(22px,2.5vw,30px)] tracking-tight text-namke-darkGreen">
                  {selectedGroceryCategory || t('store.all')}
                </h2>
                <span className="font-sub text-sm text-namke-black/66">
                  {t('store.productCount', { count: visibleProducts.length })}
                </span>
                <label className="ml-auto inline-flex items-center gap-2 bg-namke-cream rounded-full px-4 py-2.5 font-sub text-sm">
                  {t('store.sortBy')}
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    aria-label={t('store.sortBy')}
                    className="bg-transparent font-sub text-sm font-semibold text-namke-black outline-none"
                  >
                    <option value="default">{t('store.sortDefault')}</option>
                    <option value="price_asc">{t('store.sortPriceAsc')}</option>
                    <option value="price_desc">{t('store.sortPriceDesc')}</option>
                  </select>
                </label>
              </div>

              <GroceryShopSection
                products={pagedProducts}
                loading={groceryShopLoading}
                totalCount={visibleProducts.length}
                currentPage={pageSafe}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Store;
